import { z } from 'zod'
import { customer } from './customer'
import { person } from './person'
import { project } from './project'
import { task } from './task'
import { timeEntry } from './time-entry'

export const backupFormat = 'c-sheet-backup'
export const backupVersion = 1

export const backupData = z.object({
  people: z.array(person),
  customers: z.array(customer),
  projects: z.array(project),
  tasks: z.array(task),
  timeEntries: z.array(timeEntry)
})
export type BackupData = z.infer<typeof backupData>

export const backupFile = z.object({
  format: z.literal(backupFormat),
  version: z.literal(backupVersion),
  exportedAt: z.iso.datetime(),
  data: backupData
})
export type BackupFile = z.infer<typeof backupFile>

/** Restoring a kept copy needs the server's restore passcode, like restoring a file. */
export const restoreSnapshotInput = z.object({
  id: z.string().min(1),
  passcode: z.string()
})

export type RestoreSnapshotInput = z.infer<typeof restoreSnapshotInput>

export type BackupSummary = {
  people: number
  customers: number
  projects: number
  tasks: number
  timeEntries: number
  totalMinutes: number
  firstWorkDate: string | null
  lastWorkDate: string | null
}

export function summarizeBackup(data: BackupData): BackupSummary {
  const workDates = data.timeEntries.map((entry) => entry.workDate).sort()
  return {
    people: data.people.length,
    customers: data.customers.length,
    projects: data.projects.length,
    tasks: data.tasks.length,
    timeEntries: data.timeEntries.length,
    totalMinutes: data.timeEntries.reduce((sum, entry) => sum + entry.durationMinutes, 0),
    firstWorkDate: workDates[0] ?? null,
    lastWorkDate: workDates.at(-1) ?? null
  }
}

// Returns human-readable problems; an empty list means the data can be restored as a whole.
export function findBackupProblems(data: BackupData): string[] {
  const problems: string[] = []
  const report = (message: string): void => {
    if (problems.length < 50) problems.push(message)
  }

  for (const [label, rows] of [
    ['person', data.people],
    ['client', data.customers],
    ['project', data.projects],
    ['task', data.tasks],
    ['time entry', data.timeEntries]
  ] as const) {
    const seen = new Set<string>()
    for (const row of rows) {
      if (seen.has(row.id)) report(`The ${label} ${row.id} appears more than once.`)
      seen.add(row.id)
    }
  }

  const personIds = new Set(data.people.map((row) => row.id))
  const customerIds = new Set(data.customers.map((row) => row.id))
  const projectIds = new Set(data.projects.map((row) => row.id))
  const taskProjects = new Map(data.tasks.map((row) => [row.id, row.projectId]))

  for (const row of data.projects) {
    if (row.customerId && !customerIds.has(row.customerId)) {
      report(`Project "${row.name}" refers to a missing client.`)
    }
  }
  for (const row of data.tasks) {
    if (!projectIds.has(row.projectId)) report(`Task "${row.name}" refers to a missing project.`)
  }
  for (const row of data.timeEntries) {
    if (!personIds.has(row.personId)) {
      report(`The entry on ${row.workDate} refers to a missing person.`)
    }
    if (!projectIds.has(row.projectId)) {
      report(`The entry on ${row.workDate} refers to a missing project.`)
    }
    if (row.taskId && taskProjects.get(row.taskId) !== row.projectId) {
      report(`The entry on ${row.workDate} has a task that does not belong to its project.`)
    }
  }
  return problems
}
